
import { useState, useCallback } from 'react';
import { COUPON_TABS, COUPON_STATUS, FILTER_BENEFITS_TYPES } from '../../../utils/enums';

export const useCouponsFilter = (INITIAL_FILTERS) => {
  const [activeTab, setActiveTab] = useState(COUPON_TABS.PAID);
  const [searchText, setSearchText] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);

  const [pendingPaidFilters, setPendingPaidFilters] = useState(INITIAL_FILTERS);
  const [appliedPaidFilters, setAppliedPaidFilters] = useState(INITIAL_FILTERS);

  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [cancelAnchorEl, setCancelAnchorEl] = useState(null);

  const toggleFilter = useCallback((selectedFilter, isPaidTab) => {
    if (isPaidTab) {
      setPendingPaidFilters({ selectedFilter });
    }
  }, []);

  const applyFilters = useCallback(() => {
    if (activeTab === COUPON_TABS.PAID) {
      setAppliedPaidFilters(pendingPaidFilters);
    }
  }, [activeTab, pendingPaidFilters]);

  const resetFilters = useCallback(() => {
    const resetVal = { selectedFilter: FILTER_BENEFITS_TYPES.ACTIVE };
    if (activeTab === COUPON_TABS.PAID) {
      setPendingPaidFilters(resetVal);
    }
  }, [activeTab]);

  const setFilterDirectly = useCallback((filterType) => {
    if (activeTab === COUPON_TABS.PAID) {
      const newFilters = { selectedFilter: filterType };
      setPendingPaidFilters(newFilters);
      setAppliedPaidFilters(newFilters);
    }
  }, [activeTab]);

  const discardPendingChanges = useCallback(() => {
    if (activeTab === COUPON_TABS.PAID) {
      setPendingPaidFilters(appliedPaidFilters);
    }
  }, [activeTab, appliedPaidFilters]);

  const filterBenefits = useCallback((rawData, filters, isPaid) => {
    if (!rawData) return [];
  
    return rawData.map(benefit => {
      if (isPaid) {
        const matchesSearch = !searchText ||
          benefit.name.toLowerCase().includes(searchText.toLowerCase());
        
        if (!matchesSearch) {
          return { ...benefit, count: 0 };
        }
  
        return benefit;
      } 
      
      else {
        let totalSavings = 0;
        
        const filteredOrders = benefit.orders?.map(order => {
          const filteredItems = order.order_items?.filter(item => {
            const matchesSearch = !searchText ||
              benefit.name.toLowerCase().includes(searchText.toLowerCase()) ||
              String(order.order_id || '').includes(searchText) ||
              String(item.coupon_code || '').includes(searchText);
  
            if (!matchesSearch) return false;
            return true;
          });
  
          filteredItems?.forEach(item => {
            if (benefit.isMultipass) {
              totalSavings += (Number(item.original_price || 0) - Number(item.price_paid || 0));
            } else {
              const savingPerUnit = (Number(benefit.priceBeforeDiscount || 0) - Number(benefit.priceAfterDiscount || 0));
              totalSavings += savingPerUnit > 0 ? savingPerUnit : 0;
            }
          });
  
          return { ...order, order_items: filteredItems };
        }).filter(order => order.order_items?.length > 0);
  
        const totalItemsCount = filteredOrders?.reduce((acc, o) => acc + (o.order_items?.length || 0), 0) || 0;
  
        return { 
          ...benefit, 
          orders: filteredOrders, 
          count: totalItemsCount, 
          totalSavings: totalSavings 
        };
      }
    }).filter(benefit => {
      if (isPaid) {
        return benefit.count > 0;
      }
      return benefit.orders?.length > 0;
    });
  }, [searchText]);

  return {
    activeTab, setActiveTab,
    searchText, setSearchText,
    filterOpen, setFilterOpen,
    filterAnchorEl, setFilterAnchorEl,
    appliedPaidFilters,
    pendingFilters: activeTab === COUPON_TABS.PAID ? pendingPaidFilters : {},
    appliedFilters: activeTab === COUPON_TABS.PAID ? appliedPaidFilters : {},
    archiveModalOpen, setArchiveModalOpen,
    cancelModalOpen, setCancelModalOpen,
    selectedCoupon, setSelectedCoupon,
    cancelAnchorEl, setCancelAnchorEl,
    toggleFilter, applyFilters, resetFilters, filterBenefits, setFilterDirectly, discardPendingChanges
  };
};
